import React from 'react';
import {
  BuildingOfficeIcon,
  UserIcon,
  MapPinIcon,
  PhotoIcon,
  CalendarIcon,
  CheckCircleIcon,
  SparklesIcon,
  ComputerDesktopIcon,
  DevicePhoneMobileIcon,
  EyeIcon,
  CloudArrowUpIcon,
  GlobeAltIcon,
  IdentificationIcon,
  ArrowRightIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';

export default function CompanyInformationHelp() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-cyan-50 via-white to-blue-50">
      <div className="max-w-6xl mx-auto px-4 py-12">
        {/* Header Section */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-cyan-500 to-blue-600 rounded-full mb-6">
            <BuildingOfficeIcon className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Company Information Setup</h1>
          <p className="text-lg md:text-xl text-gray-600 max-w-2xl mx-auto">
            Your first step to personalize the system with your business details
          </p>
        </div>

        {/* Overview Card */}
        <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8 mb-8 border border-gray-100">
          <div className="flex items-start space-x-2 md:space-x-4">
            <div className="flex-shrink-0">
              <div className="w-12 h-12 bg-cyan-100 rounded-lg flex items-center justify-center">
                <SparklesIcon className="w-6 h-6 text-cyan-600" />
              </div>
            </div>
            <div>
              <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-3">What is the Company Information Form?</h2>
              <p className="text-gray-600 text-base md:text-lg leading-relaxed">
                This is your foundation step - a simple form where you tell the system about your business and yourself. It's like introducing your company to the platform, ensuring everything gets personalized and set up correctly from day one.
              </p>
            </div>
          </div>
        </div>

        {/* Step-by-Step Form Process */}
        <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8 mb-8 border border-gray-100">
          <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-6 md:mb-8 text-center">Step-by-Step Setup Process</h2>

          <div className="relative">
            {/* Connection Line */}
            <div className="absolute top-8 left-0 right-0 h-0.5 bg-gradient-to-r from-cyan-200 via-blue-200 to-cyan-200 hidden md:block"></div>

            <div className="grid md:grid-cols-5 gap-6 md:gap-8">
              {/* Step 1 */}
              <div className="relative">
                <div className="bg-cyan-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4 border-4 border-white shadow-lg">
                  <span className="text-cyan-600 font-bold text-xl">1</span>
                </div>
                <div className="text-center">
                  <h3 className="font-semibold text-gray-900 mb-2">Company Basics</h3>
                  <p className="text-sm text-gray-600">Enter company name, website, and industry</p>
                  <div className="mt-3 p-3 bg-cyan-50 rounded-lg border border-cyan-200">
                    <BuildingOfficeIcon className="w-6 h-6 text-cyan-600 mx-auto mb-1" />
                    <p className="text-xs text-cyan-700 font-medium">Company Info</p>
                  </div>
                </div>
              </div>

              {/* Arrow */}
              <div className="hidden md:flex items-center justify-center">
                <ArrowRightIcon className="w-6 h-6 text-cyan-400" />
              </div>

              {/* Step 2 */}
              <div className="relative">
                <div className="bg-blue-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4 border-4 border-white shadow-lg">
                  <span className="text-blue-600 font-bold text-xl">2</span>
                </div>
                <div className="text-center">
                  <h3 className="font-semibold text-gray-900 mb-2">Add Address</h3>
                  <p className="text-sm text-gray-600">Fill business address and geo-tag location</p>
                  <div className="mt-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
                    <MapPinIcon className="w-6 h-6 text-blue-600 mx-auto mb-1" />
                    <p className="text-xs text-blue-700 font-medium">Location</p>
                  </div>
                </div>
              </div>

              {/* Arrow */}
              <div className="hidden md:flex items-center justify-center">
                <ArrowRightIcon className="w-6 h-6 text-blue-400" />
              </div>

              {/* Step 3 */}
              <div className="relative">
                <div className="bg-indigo-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4 border-4 border-white shadow-lg">
                  <span className="text-indigo-600 font-bold text-xl">3</span>
                </div>
                <div className="text-center">
                  <h3 className="font-semibold text-gray-900 mb-2">Upload Logo</h3>
                  <p className="text-sm text-gray-600">Add company logo for branding</p>
                  <div className="mt-3 p-3 bg-indigo-50 rounded-lg border border-indigo-200">
                    <PhotoIcon className="w-6 h-6 text-indigo-600 mx-auto mb-1" />
                    <p className="text-xs text-indigo-700 font-medium">Logo</p>
                  </div>
                </div>
              </div>

              {/* Arrow */}
              <div className="hidden md:flex items-center justify-center">
                <ArrowRightIcon className="w-6 h-6 text-indigo-400" />
              </div>

              {/* Step 4 */}
              <div className="relative">
                <div className="bg-green-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4 border-4 border-white shadow-lg">
                  <span className="text-green-600 font-bold text-xl">4</span>
                </div>
                <div className="text-center">
                  <h3 className="font-semibold text-gray-900 mb-2">Personal Info</h3>
                  <p className="text-sm text-gray-600">Add your details and role</p>
                  <div className="mt-3 p-3 bg-green-50 rounded-lg border border-green-200">
                    <UserIcon className="w-6 h-6 text-green-600 mx-auto mb-1" />
                    <p className="text-xs text-green-700 font-medium">Your Info</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Final Step */}
            <div className="mt-8 text-center">
              <div className="flex flex-wrap justify-center items-center space-x-2 md:space-x-4">
                <ArrowRightIcon className="w-6 h-6 text-green-400" />
                <div className="bg-purple-100 rounded-full w-16 h-16 flex items-center justify-center border-4 border-white shadow-lg">
                  <span className="text-purple-600 font-bold text-xl">5</span>
                </div>
                <ArrowRightIcon className="w-6 h-6 text-purple-400" />
              </div>
              <div className="mt-4">
                <h3 className="font-semibold text-gray-900 mb-2">Submit & Complete</h3>
                <p className="text-sm text-gray-600">Review and submit to finish setup</p>
                <div className="mt-3 inline-flex items-center space-x-3">
                  <div className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg">
                    <XMarkIcon className="w-4 h-4 inline mr-1" />
                    Cancel
                  </div>
                  <div className="px-4 py-2 bg-purple-500 text-white rounded-lg">
                    <CheckCircleIcon className="w-4 h-4 inline mr-1" />
                    Submit
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Form Layout Diagram */}
        <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8 mb-8 border border-gray-100">
          <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-4 md:mb-6 text-center">Form Layout</h2>

          {/* Desktop Layout */}
          <div className="mb-8">
            <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
              <ComputerDesktopIcon className="w-5 h-5 mr-2 text-cyan-600" />
              Desktop View
            </h3>
            <div className="bg-gray-50 rounded-xl p-6 border-2 border-dashed border-gray-300">
              <div className="space-y-6">
                {/* Company Profile Section */}
                <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                  <div className="flex items-center space-x-2 mb-3">
                    <BuildingOfficeIcon className="w-5 h-5 text-blue-600" />
                    <h4 className="font-semibold text-blue-800">Company Profile</h4>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <div className="w-full h-3 bg-blue-200 rounded"></div>
                      <div className="w-3/4 h-3 bg-blue-200 rounded"></div>
                      <div className="w-1/2 h-3 bg-blue-200 rounded"></div>
                      <div className="w-2/3 h-3 bg-blue-200 rounded"></div>
                    </div>
                    <div className="space-y-2">
                      <div className="flex space-x-2">
                        <div className="w-16 h-8 bg-blue-300 rounded"></div>
                        <div className="w-16 h-8 bg-blue-300 rounded"></div>
                      </div>
                      <div className="w-full h-3 bg-blue-200 rounded"></div>
                      <div className="w-4/5 h-3 bg-blue-200 rounded"></div>
                    </div>
                  </div>
                  <p className="text-xs text-blue-700 mt-2 font-medium">Business Details & Address</p>
                </div>

                {/* Your Info Section */}
                <div className="bg-green-50 rounded-lg p-4 border border-green-200">
                  <div className="flex items-center space-x-2 mb-3">
                    <UserIcon className="w-5 h-5 text-green-600" />
                    <h4 className="font-semibold text-green-800">Your Information</h4>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <div className="w-full h-3 bg-green-200 rounded"></div>
                      <div className="w-3/4 h-3 bg-green-200 rounded"></div>
                      <div className="w-1/2 h-3 bg-green-200 rounded"></div>
                    </div>
                    <div className="space-y-2">
                      <div className="w-2/3 h-3 bg-green-200 rounded"></div>
                      <div className="w-16 h-8 bg-green-300 rounded"></div>
                    </div>
                  </div>
                  <p className="text-xs text-green-700 mt-2 font-medium">Personal Details & Role</p>
                </div>

                {/* Action Buttons */}
                <div className="flex justify-end space-x-3">
                  <div className="w-20 h-10 bg-gray-200 rounded-lg flex items-center justify-center">
                    <XMarkIcon className="w-4 h-4 text-gray-600" />
                  </div>
                  <div className="w-24 h-10 bg-blue-500 rounded-lg flex items-center justify-center">
                    <ArrowRightIcon className="w-4 h-4 text-white" />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Mobile Layout */}
          <div>
            <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
              <DevicePhoneMobileIcon className="w-5 h-5 mr-2 text-blue-600" />
              Mobile View
            </h3>
            <div className="bg-gray-50 rounded-xl p-6 border-2 border-dashed border-gray-300 max-w-xs mx-auto">
              <div className="space-y-4">
                <div className="bg-blue-50 rounded p-3 border border-blue-200">
                  <div className="flex items-center space-x-2 mb-2">
                    <BuildingOfficeIcon className="w-4 h-4 text-blue-600" />
                    <span className="text-sm font-medium text-blue-800">Company</span>
                  </div>
                  <div className="space-y-1">
                    <div className="w-full h-2 bg-blue-200 rounded"></div>
                    <div className="w-3/4 h-2 bg-blue-200 rounded"></div>
                    <div className="w-1/2 h-2 bg-blue-200 rounded"></div>
                  </div>
                </div>

                <div className="bg-green-50 rounded p-3 border border-green-200">
                  <div className="flex items-center space-x-2 mb-2">
                    <UserIcon className="w-4 h-4 text-green-600" />
                    <span className="text-sm font-medium text-green-800">Your Info</span>
                  </div>
                  <div className="space-y-1">
                    <div className="w-full h-2 bg-green-200 rounded"></div>
                    <div className="w-2/3 h-2 bg-green-200 rounded"></div>
                  </div>
                </div>

                <div className="flex justify-end space-x-2">
                  <div className="w-16 h-8 bg-gray-200 rounded"></div>
                  <div className="w-18 h-8 bg-blue-500 rounded"></div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Form Sections */}
        <div className="grid md:grid-cols-2 gap-6 md:gap-8 mb-8">
          {/* Company Profile Section */}
          <div className="bg-white rounded-xl shadow-md p-4 md:p-6 border border-gray-100">
            <div className="flex items-center space-x-3 mb-4">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <BuildingOfficeIcon className="w-5 h-5 text-blue-600" />
              </div>
              <h3 className="text-base md:text-lg font-semibold text-gray-900">Company Profile</h3>
            </div>

            <div className="space-y-3">
              <div className="flex items-center space-x-3">
                <IdentificationIcon className="w-4 h-4 text-gray-500" />
                <span className="text-sm text-gray-700">Company Name</span>
              </div>
              <div className="flex items-center space-x-3">
                <GlobeAltIcon className="w-4 h-4 text-gray-500" />
                <span className="text-sm text-gray-700">Website URL</span>
              </div>
              <div className="flex items-center space-x-3">
                <BuildingOfficeIcon className="w-4 h-4 text-gray-500" />
                <span className="text-sm text-gray-700">Industry Type</span>
              </div>
              <div className="flex items-center space-x-3">
                <IdentificationIcon className="w-4 h-4 text-gray-500" />
                <span className="text-sm text-gray-700">Companies House Number</span>
              </div>
              <div className="flex items-center space-x-3">
                <MapPinIcon className="w-4 h-4 text-gray-500" />
                <span className="text-sm text-gray-700">Full Address</span>
              </div>
              <div className="flex items-center space-x-3">
                <MapPinIcon className="w-4 h-4 text-gray-500" />
                <span className="text-sm text-gray-700">Geo Tag Location</span>
              </div>
              <div className="flex items-center space-x-3">
                <PhotoIcon className="w-4 h-4 text-gray-500" />
                <span className="text-sm text-gray-700">Company Logo Upload</span>
              </div>
            </div>
          </div>

          {/* Your Info Section */}
          <div className="bg-white rounded-xl shadow-md p-4 md:p-6 border border-gray-100">
            <div className="flex items-center space-x-3 mb-4">
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                <UserIcon className="w-5 h-5 text-green-600" />
              </div>
              <h3 className="text-base md:text-lg font-semibold text-gray-900">Your Information</h3>
            </div>

            <div className="space-y-3">
              <div className="flex items-center space-x-3">
                <UserIcon className="w-4 h-4 text-gray-500" />
                <span className="text-sm text-gray-700">First & Last Name</span>
              </div>
              <div className="flex items-center space-x-3">
                <IdentificationIcon className="w-4 h-4 text-gray-500" />
                <span className="text-sm text-gray-700">Role in Company</span>
              </div>
              <div className="flex items-center space-x-3">
                <GlobeAltIcon className="w-4 h-4 text-gray-500" />
                <span className="text-sm text-gray-700">Telephone Number</span>
              </div>
              <div className="flex items-center space-x-3">
                <CalendarIcon className="w-4 h-4 text-gray-500" />
                <span className="text-sm text-gray-700">Start Date</span>
              </div>
            </div>
          </div>
        </div>

        {/* Special Features */}
        <div className="grid md:grid-cols-3 gap-4 md:gap-6 mb-8">
          <div className="bg-white rounded-xl shadow-md p-4 md:p-6 border border-gray-100 hover:shadow-lg transition-shadow">
            <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mb-4">
              <MapPinIcon className="w-6 h-6 text-purple-600" />
            </div>
            <h3 className="text-base md:text-lg font-semibold text-gray-900 mb-2">Location Tagging</h3>
            <p className="text-gray-600 text-sm md:text-base">One-click button to automatically add your business location using GPS</p>
          </div>

          <div className="bg-white rounded-xl shadow-md p-4 md:p-6 border border-gray-100 hover:shadow-lg transition-shadow">
            <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center mb-4">
              <CloudArrowUpIcon className="w-6 h-6 text-orange-600" />
            </div>
            <h3 className="text-base md:text-lg font-semibold text-gray-900 mb-2">Logo Upload</h3>
            <p className="text-gray-600 text-sm md:text-base">Drag-and-drop or click to upload your company logo for branding</p>
          </div>

          <div className="bg-white rounded-xl shadow-md p-4 md:p-6 border border-gray-100 hover:shadow-lg transition-shadow">
            <div className="w-12 h-12 bg-indigo-100 rounded-lg flex items-center justify-center mb-4">
              <CalendarIcon className="w-6 h-6 text-indigo-600" />
            </div>
            <h3 className="text-base md:text-lg font-semibold text-gray-900 mb-2">Date Picker</h3>
            <p className="text-gray-600 text-sm md:text-base">Easy calendar widget for selecting dates like your start date</p>
          </div>
        </div>

        {/* Benefits Section */}
        <div className="bg-gradient-to-r from-cyan-500 to-blue-600 rounded-2xl p-6 md:p-8 text-white mb-8">
          <h2 className="text-xl md:text-2xl font-bold mb-4 md:mb-6">Why This Matters</h2>
          <div className="grid md:grid-cols-2 gap-4 md:gap-6">
            <div className="flex items-start space-x-3">
              <CheckCircleIcon className="w-6 h-6 text-green-300 flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-semibold mb-1">Personalized Experience</h3>
                <p className="text-cyan-100">Everything gets customized to your business and role</p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <CheckCircleIcon className="w-6 h-6 text-green-300 flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-semibold mb-1">Professional Branding</h3>
                <p className="text-cyan-100">Your logo and details make the system feel like yours</p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <CheckCircleIcon className="w-6 h-6 text-green-300 flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-semibold mb-1">Location Features</h3>
                <p className="text-cyan-100">Geo-tagging enables location-based tools and maps</p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <CheckCircleIcon className="w-6 h-6 text-green-300 flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-semibold mb-1">Compliance Ready</h3>
                <p className="text-cyan-100">Includes necessary business details for legal purposes</p>
              </div>
            </div>
          </div>
        </div>

        {/* Getting Started */}
        <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8 border border-gray-100">
          <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-4 md:mb-6 flex items-center">
            <SparklesIcon className="w-6 h-6 mr-3 text-cyan-500" />
            How to Fill It Out
          </h2>
          <div className="space-y-4">
            <div className="flex items-start space-x-2 md:space-x-4">
              <div className="flex-shrink-0 w-8 h-8 bg-cyan-100 rounded-full flex items-center justify-center">
                <span className="text-cyan-600 font-bold text-sm">1</span>
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">Company Details First</h3>
                <p className="text-gray-600">Start with your company name, website, and basic business information</p>
              </div>
            </div>
            <div className="flex items-start space-x-2 md:space-x-4">
              <div className="flex-shrink-0 w-8 h-8 bg-cyan-100 rounded-full flex items-center justify-center">
                <span className="text-cyan-600 font-bold text-sm">2</span>
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">Add Your Address</h3>
                <p className="text-gray-600">Fill in the complete business address and click geo-tag for location</p>
              </div>
            </div>
            <div className="flex items-start space-x-2 md:space-x-4">
              <div className="flex-shrink-0 w-8 h-8 bg-cyan-100 rounded-full flex items-center justify-center">
                <span className="text-cyan-600 font-bold text-sm">3</span>
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">Upload Logo</h3>
                <p className="text-gray-600">Add your company logo to personalize the experience</p>
              </div>
            </div>
            <div className="flex items-start space-x-2 md:space-x-4">
              <div className="flex-shrink-0 w-8 h-8 bg-cyan-100 rounded-full flex items-center justify-center">
                <span className="text-cyan-600 font-bold text-sm">4</span>
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">Your Personal Info</h3>
                <p className="text-gray-600">Add your details, role, and start date using the calendar picker</p>
              </div>
            </div>
            <div className="flex items-start space-x-2 md:space-x-4">
              <div className="flex-shrink-0 w-8 h-8 bg-cyan-100 rounded-full flex items-center justify-center">
                <span className="text-cyan-600 font-bold text-sm">5</span>
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">Review & Submit</h3>
                <p className="text-gray-600">Double-check everything and click Submit to save and continue</p>
              </div>
            </div>
          </div>

          <div className="mt-8 grid md:grid-cols-2 gap-4">
            <div className="p-4 bg-green-50 rounded-lg border border-green-200">
              <div className="flex items-center space-x-3">
                <EyeIcon className="w-5 h-5 text-green-600" />
                <div>
                  <h3 className="font-semibold text-green-800">Auto-Save</h3>
                  <p className="text-green-700 text-sm">Your entries are saved as you type</p>
                </div>
              </div>
            </div>

            <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
              <div className="flex items-center space-x-3">
                <CheckCircleIcon className="w-5 h-5 text-blue-600" />
                <div>
                  <h3 className="font-semibold text-blue-800">Validation</h3>
                  <p className="text-blue-700 text-sm">Required fields are checked before submission</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}